import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import AudioTranscriptText from '@modules/feedback/components/AudioType/AudioTranscript/AudioTranscriptText';
import {
  PLAYING_AUDIO_MESSAGE_HIGHLIGHT_CLASSNAME,
  SHARING_AUDIO_MESSAGE_HIGHLIGHT_CLASSNAME,
} from '@modules/feedback/components/AudioType/AudioTranscript/constants';
import {
  getNoAudioSourceInfoText,
  isEmptyAudioSourceFeedback,
  trackAudioFeedbackEvents,
} from '@modules/feedback/components/AudioType/utils';
import { getHighlightAnimationKeyframes } from '@modules/feedback/components/AudioType/utils/getHighlightAnimationKeyframes';
import useFeedbackListStore, {
  FeedbackListStore,
} from '@modules/feedback/store/useFeedbackListStore';
import { Highlight } from '@modules/feedback/types';
import { getSnippetShareUrl } from '@modules/feedback/utils';
import { PlayArrowRounded, ShareRounded } from '@mui/icons-material';
import { Box, Fab, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import { useCallback, useMemo } from 'react';
import {
  Feedback,
  FeedbackContentType,
  FeedbackLabel,
} from 'v2/core/entities/feedback';
import { useGetUser } from 'v2/core/services/user';
import { shallow } from 'zustand/shallow';

const getFeedbackListStoreState = (state: FeedbackListStore) => {
  return {
    setSelectedSeekJumpTime: state.setSelectedSeekJumpTime,
  };
};

interface AudioTranscriptProps {
  feedback: Feedback;
  showOriginalLanguage: boolean;
  highlights: { [key: string]: Highlight[] };
  selectedLabels?: FeedbackLabel[];
}

const AudioTranscript = ({
  feedback,
  showOriginalLanguage,
  highlights,
  selectedLabels = [],
}: AudioTranscriptProps) => {
  const theme = useTheme();
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();
  const { showSuccessNotification } = useNotificationContext();

  const { setSelectedSeekJumpTime } = useFeedbackListStore(
    getFeedbackListStoreState,
    shallow,
  );

  const animationKeyframes = useMemo(
    () =>
      getHighlightAnimationKeyframes(
        theme.palette.primary.main,
        theme.palette.primary.dark,
      ),
    [theme.palette.primary.dark, theme.palette.primary.main],
  );

  const handleOnPlayMessageAudio = useCallback(
    (startTime: number) => {
      setSelectedSeekJumpTime(startTime, feedback.id);
      trackAudioFeedbackEvents({
        eventName: 'Audio Feedback - Transcript: Play Button Clicked',
        feedbackID: feedback.id,
      });
    },
    [feedback.id, setSelectedSeekJumpTime],
  );

  const handleOnShareMessageSnippet = useCallback(
    (startTime: number, endTime: number) => {
      const snippetShareUrl = getSnippetShareUrl({
        feedback,
        selectedLabels,
        startTime,
        endTime,
        tenantSlug,
      });

      navigator.clipboard.writeText(snippetShareUrl);
      showSuccessNotification({
        contentComponent: `Snippet link copied to the clipboard.`,
        props: { isClosable: true },
      });

      trackAudioFeedbackEvents({
        eventName: 'Audio Feedback - Transcript: Share Button Clicked',
        feedbackID: feedback.id,
      });
    },
    [feedback, selectedLabels, showSuccessNotification, tenantSlug],
  );

  if (feedback.content.type !== FeedbackContentType.Audio) {
    return null;
  }

  const conversation = showOriginalLanguage
    ? feedback.content.audioConversationOriginal
    : feedback.content.audioConversationEn;

  const isAudioSourceEmpty = isEmptyAudioSourceFeedback(feedback);

  return (
    <Box>
      <Stack spacing={2}>
        {conversation?.map((conversationUnit, index) => {
          const isUserActor = conversationUnit.actor === 'User';

          const shouldShowName =
            index === 0 ||
            conversation[index - 1].actorName !== conversationUnit.actorName;

          const shouldAlignToRight = isUserActor;

          return (
            <Box key={index}>
              {shouldShowName && (
                <Stack
                  direction="row"
                  justifyContent={
                    !shouldAlignToRight ? 'flex-end' : 'flex-start'
                  }
                  mb={1}
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    component="p"
                  >
                    {conversationUnit.actorName}
                  </Typography>
                </Stack>
              )}
              <Stack
                direction={!shouldAlignToRight ? 'row' : 'row-reverse'}
                justifyContent="flex-end"
                alignItems="center"
                sx={{
                  '&:hover': {
                    '.audio-message-bubble-action-pill': {
                      opacity: 1,
                    },
                  },
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    padding: 1,
                    borderRadius: 8,
                    opacity: 0,
                    marginRight: !shouldAlignToRight ? '-10px' : 'auto',
                    marginLeft: shouldAlignToRight ? '-10px' : 'auto',
                    zIndex: 2,
                    backgroundColor: !isUserActor
                      ? 'feedback.chatBubble.primary'
                      : 'feedback.chatBubble.secondary',
                    transition: 'opacity 0.2s',
                  }}
                  className="audio-message-bubble-action-pill"
                >
                  <Stack
                    direction={!shouldAlignToRight ? 'row' : 'row-reverse'}
                    alignItems="center"
                    spacing={1}
                  >
                    <Fab
                      color="primary"
                      size="small"
                      onClick={() =>
                        handleOnShareMessageSnippet(
                          conversationUnit.metadata.startTime,
                          conversationUnit.metadata.endTime,
                        )
                      }
                      sx={{
                        width: '24px',
                        height: '24px',
                        minHeight: '24px',
                        boxShadow: 'none',
                      }}
                    >
                      <ShareRounded fontSize="small" />
                    </Fab>
                    <Tooltip
                      arrow
                      title={
                        isAudioSourceEmpty
                          ? getNoAudioSourceInfoText(feedback.sourceV2?.name)
                          : ''
                      }
                      placement="top"
                    >
                      <Box component="span">
                        <Fab
                          color="primary"
                          size="small"
                          onClick={() =>
                            handleOnPlayMessageAudio(
                              conversationUnit.metadata.startTime,
                            )
                          }
                          sx={{
                            width: '24px',
                            height: '24px',
                            minHeight: '24px',
                            boxShadow: 'none',
                          }}
                          disabled={isAudioSourceEmpty}
                        >
                          <PlayArrowRounded />
                        </Fab>
                      </Box>
                    </Tooltip>
                  </Stack>
                </Paper>
                <Paper
                  sx={{
                    padding: 3,
                    borderRadius: 4,
                    borderTopRightRadius: !shouldAlignToRight ? 1 : 16,
                    borderTopLeftRadius: !shouldAlignToRight ? 16 : 1,
                    maxWidth: '70%',
                    backgroundColor: isUserActor
                      ? 'feedback.chatBubble.primary'
                      : 'feedback.chatBubble.secondary',
                    [`&.${PLAYING_AUDIO_MESSAGE_HIGHLIGHT_CLASSNAME}, 
                    &.${SHARING_AUDIO_MESSAGE_HIGHLIGHT_CLASSNAME}`]: {
                      animation: `${animationKeyframes} 0.7s linear infinite alternate`,
                      '.MuiTypography-root': {
                        color: 'primary.contrastText',
                      },
                    },
                    boxShadow: 'none',
                  }}
                  data-audio-message-id={index.toString()}
                >
                  <AudioTranscriptText
                    index={index}
                    text={conversationUnit.text}
                    highlights={highlights}
                    showOriginalLanguage={showOriginalLanguage}
                  />
                </Paper>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default AudioTranscript;
